import { Card, Col, Empty, Flex, Result, Row, Tabs, TabsProps, Typography } from 'antd';
import { FC, memo, useMemo } from 'react';
import { WrenchAdjustableCircleFill } from 'react-bootstrap-icons';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { AppRoutes } from '@/common/interfaces';
import { CommonContainerTitle, FallbackedLayout } from '@/common/layouts';

import { AddressChangerBadge, useUpdateAddressChangingStatus } from '@/modules/AddressChanger';
import { useGetSitesSearchConsoleInfoQuery, useUpdateSCAccountProcessStatus } from '@/modules/Domains';


enum SiteSearchConsoleCategories {
  Sitemap = 'Sitemap',
  UrlInspection = 'URL Inspection',
  SearchAnalytics = 'Search Analytics',
}

interface Props {
  siteId: string;
}

export const SearchConsoleOverviewContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const tabsKeys = useMemo<Record<Uncapitalize<keyof typeof SiteSearchConsoleCategories>, string> & { none: 'none' }>(() => ({
    none: 'none',
    sitemap: AppRoutes.SiteSearchConsoleSitemap.replace(':siteId', siteId),
    urlInspection: AppRoutes.SiteSearchConsoleSearchUrlInspection.replace(':siteId', siteId),
    searchAnalytics: AppRoutes.SiteSearchConsoleSearchAnalytics.replace(':siteId', siteId),
  }), [siteId]);

  const activeKey = (() => {
    if (location.pathname.startsWith(tabsKeys.sitemap)) return tabsKeys.sitemap;
    if (location.pathname.startsWith(tabsKeys.urlInspection)) return tabsKeys.urlInspection;
    if (location.pathname.startsWith(tabsKeys.searchAnalytics)) return tabsKeys.searchAnalytics;
    return tabsKeys.none;
  })();

  const tabItems: TabsProps['items'] = useMemo(() => [
    {
      key: tabsKeys.sitemap,
      label: SiteSearchConsoleCategories.Sitemap
    },
    {
      key: tabsKeys.urlInspection,
      label: SiteSearchConsoleCategories.UrlInspection
    },
    {
      key: tabsKeys.searchAnalytics,
      label: SiteSearchConsoleCategories.SearchAnalytics
    },
  ].map(tab => ({ ...tab, children: <Outlet key={ tab.key }/> })), [siteId]);

  const {
    data: infoData,
    isFetching: isInfoFetching,
    isError: isInfoError,
    error: infoError,
  } = useGetSitesSearchConsoleInfoQuery(siteId)({
    enabled: !!siteId,
  });

  useUpdateSCAccountProcessStatus({ siteId, pageParam: null, domainId: null });

  useUpdateAddressChangingStatus({ siteId });

  const onTabChange = (key: string) => {
    navigate(key);
  };

  const handleBack = () => {
    navigate(AppRoutes.SitesById.replace(':siteId', siteId));
  };

  return (
    <Card>
      <CommonContainerTitle
        onBack={ handleBack }
        extra={
          infoData?.haveSearchConsoleAccount &&
            <AddressChangerBadge
              isLoading={ isInfoFetching }
              redirectingInfo={ infoData?.searchConsoleAddressChanging }
              siteId={ siteId }
              isAutoRedirecting={ infoData?.isAutoRedirecting ?? false }
            />
        }
      >
        Search Console сайту
      </CommonContainerTitle>

      <FallbackedLayout
        data={ infoData }
        isLoading={ isInfoFetching }
        error={ infoError }
        isError={ isInfoError }
      >
        {
          ({ haveSearchConsoleAccount }) =>
            haveSearchConsoleAccount
              ? (
                <Row gutter={ [0, 16] }>
                  <Col span={ 24 }>
                    <Tabs
                      activeKey={ activeKey }
                      items={ tabItems }
                      destroyInactiveTabPane={ false }
                      onChange={ onTabChange }
                    />
                  </Col>

                  { activeKey === 'none' && <Col span={ 24 }>
                    <Flex align="center" justify="center" wrap style={{ minHeight: 150 }}>
                      <Empty
                        description={ <Typography.Text type="secondary">Виберіть функцію Search Console</Typography.Text> }
                        image={ <WrenchAdjustableCircleFill size={ 40 }/> }
                      />
                    </Flex>
                  </Col> }
                </Row>
              )
              : (
                <Result
                  status="warning"
                  title="До сайту не підключено акаунт Search Console"
                />
              )
        }
      </FallbackedLayout>
    </Card>
  );
});
